import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { passengerApi } from "@/lib/passengerApi";
import {
  Megaphone, Warning, Info, WarningCircle, CaretLeft, MapPin, Clock,
  ArrowsClockwise,
} from "@phosphor-icons/react";

const SEV = {
  critical: { dot: "#EF4444", bg: "bg-red-50",   border: "border-red-200",   fg: "text-red-700",   icon: WarningCircle, label: "Critical" },
  warning:  { dot: "#F59E0B", bg: "bg-amber-50", border: "border-amber-200", fg: "text-amber-700", icon: Warning,       label: "Warning"  },
  info:     { dot: "#5B2EE5", bg: "bg-[#F5F2FF]",border: "border-[#DCC9FF]", fg: "text-[#4920C2]", icon: Info,          label: "Info"     },
};
const meta = (s) => SEV[s] || SEV.info;

export default function MajheAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await passengerApi.get("/service-alerts");
      setAlerts(data.alerts || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  return (
    <div data-testid="majhe-alerts">
      {/* Top bar */}
      <div className="px-5 pt-6 flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="majhe-alerts-back"
          aria-label="Back"
          className="w-10 h-10 rounded-full bg-[#EDE5FF] text-[#5B2EE5] flex items-center justify-center">
          <CaretLeft size={20} weight="bold"/>
        </button>
        <div className="flex-1">
          <div className="text-[12px] text-[#7B7390] font-semibold">Public Notices</div>
          <div className="text-[20px] font-extrabold tracking-tight text-[#18142A]">Service Alerts</div>
        </div>
        <button onClick={refresh} data-testid="majhe-alerts-refresh"
          aria-label="Refresh alerts"
          className="w-10 h-10 rounded-full bg-[#EDE5FF] text-[#5B2EE5] flex items-center justify-center">
          <ArrowsClockwise size={18} weight="bold"/>
        </button>
      </div>

      {/* Count summary */}
      <div className="px-5 mt-4">
        <div className="rounded-2xl p-4 bg-gradient-to-br from-[#5B2EE5] to-[#7C3AED] text-white">
          <div className="flex items-center gap-3">
            <Megaphone size={22} weight="duotone"/>
            <div className="flex-1">
              <div className="text-[12px] uppercase tracking-wider opacity-80 font-bold">Right now</div>
              <div className="text-[22px] font-extrabold leading-tight">
                {loading ? "…" : alerts.length}
                <span className="text-[14px] opacity-80 font-bold ml-1">live alert{alerts.length !== 1 ? "s" : ""}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="px-5 mt-5 space-y-3 pb-10">
        {!loading && alerts.length === 0 && (
          <div className="rounded-2xl border border-[#E4DEFF] bg-[#FBFAFF] p-6 text-center" data-testid="majhe-alerts-empty">
            <Info size={32} weight="duotone" className="text-[#5B2EE5] mx-auto"/>
            <div className="mt-2 text-sm font-bold text-[#18142A]">All clear</div>
            <div className="text-[12px] text-[#7B7390] mt-1">
              There are no service alerts or route diversions at the moment.
              Travel safe!
            </div>
          </div>
        )}
        {alerts.map((a) => <AlertCard key={a.id} a={a}/>)}
      </div>
    </div>
  );
}

function AlertCard({ a }) {
  const s = meta(a.severity);
  const Icon = s.icon;
  return (
    <article className={`rounded-2xl border ${s.border} ${s.bg} p-4`}
              data-testid={`majhe-alert-${a.id}`}
              aria-label={`${s.label} alert: ${a.title}`}>
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center`}
             style={{ background: s.dot, color: "#fff" }}>
          <Icon size={18} weight="fill" aria-hidden="true"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-[10px] uppercase tracking-wider font-bold ${s.fg}`}>
            {s.label} · {a.type}
          </div>
          <h3 className="text-[15px] font-extrabold text-[#18142A] leading-snug mt-0.5">
            {a.title}
          </h3>
        </div>
      </div>
      {a.message && (
        <p className="text-[13px] text-[#3D3559] mt-2 leading-relaxed whitespace-pre-wrap">{a.message}</p>
      )}
      {a.alternative && (
        <div className="mt-3 px-3 py-2 bg-white/60 border border-white rounded-lg">
          <div className="text-[10px] uppercase tracking-wider text-[#7B7390] font-bold">Suggested alternative</div>
          <div className="text-[12.5px] text-[#18142A] mt-0.5">{a.alternative}</div>
        </div>
      )}
      {(a.affected_routes?.length > 0 || a.affected_stop_ids?.length > 0) && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {a.affected_routes?.map((r) => (
            <span key={r} className="text-[10.5px] font-bold font-mono bg-white border border-[#DCC9FF] text-[#5B2EE5] rounded-full px-2 py-0.5 inline-flex items-center gap-1">
              <MapPin size={10} weight="bold"/> {r}
            </span>
          ))}
          {a.affected_stop_ids?.slice(0, 5).map((id) => (
            <span key={id} className="text-[10.5px] font-mono bg-white border border-slate-200 text-slate-700 rounded-full px-2 py-0.5">
              Stop #{id}
            </span>
          ))}
          {a.affected_stop_ids?.length > 5 && (
            <span className="text-[10.5px] text-[#7B7390]">+{a.affected_stop_ids.length - 5} more stops</span>
          )}
        </div>
      )}
      {(a.starts_at || a.ends_at) && (
        <div className="mt-3 flex items-center gap-2 text-[11px] text-[#7B7390]">
          <Clock size={11} weight="bold" aria-hidden="true"/>
          {a.starts_at && <span>From {new Date(a.starts_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>}
          {a.ends_at && <span>· Until {new Date(a.ends_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>}
        </div>
      )}
    </article>
  );
}
